/* ============================================================
   Modulo: Statement Bar — seamless ticker.

   Mirrors the announcement-bar engine (own copy, keyed data-modulo-as —
   the announcement-drop / marquee-drop precedent):
   - Measure one cycle = width of the original items + their gaps.
   - Clone the set until the track >= viewport + 1 cycle, so off-screen
     content is always queued -> no gap, no jump on loop, with any
     number/length of messages.
   - Set --modulo-as-cycle-width so CSS translates by exact pixels and
     lands on a clean boundary every loop.
   - Mark ready=1 so the CSS animation attaches (no jumpy first paint).
   - Under prefers-reduced-motion we don't clone or animate at all;
     CSS wraps + centers the original messages instead.

   Note: window.moduloLazyInit is deliberately NOT used — it does not
   exist on disk (modulo-core.js is absent) and calling it is what
   live-bugged section-stats-progress.
   ============================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var MQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(MQ && MQ.matches); }

  function getGap(el) {
    var style = getComputedStyle(el);
    var raw = style.columnGap || style.gap || '0';
    var n = parseFloat(raw);
    return isNaN(n) ? 0 : n;
  }

  function measureCycle(items, gap) {
    var w = 0;
    for (var i = 0; i < items.length; i++) {
      w += items[i].getBoundingClientRect().width;
    }
    // One gap per item so the loop boundary stays aligned between repeats.
    return w + gap * items.length;
  }

  function cloneSet(track, items) {
    for (var i = 0; i < items.length; i++) {
      var clone = items[i].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('role');
      clone.setAttribute('data-modulo-as-clone', '');
      track.appendChild(clone);
    }
  }

  function originals(track) {
    var out = [];
    var kids = track.children;
    for (var i = 0; i < kids.length; i++) {
      if (!kids[i].hasAttribute('data-modulo-as-clone')) out.push(kids[i]);
    }
    return out;
  }

  function dropClones(track) {
    var clones = track.querySelectorAll('[data-modulo-as-clone]');
    for (var i = 0; i < clones.length; i++) clones[i].remove();
  }

  /* Measure + clone from a clean slate. Safe to call repeatedly: the cycle
     width is re-derived every time, which is what keeps the loop seamless
     after a breakpoint changes gap (<=480px) or font-size (<=860px). */
  function build(el) {
    if (reduced()) return;
    var track = el.querySelector('.modulo-as__track');
    if (!track) return;

    dropClones(track);

    var items = originals(track);
    if (items.length === 0) return;

    var gap = getGap(track);
    var cycle = measureCycle(items, gap);
    if (cycle <= 0) return; // layout not ready

    var viewport = el.offsetWidth || document.documentElement.clientWidth;
    var copiesNeeded = Math.max(2, Math.ceil((viewport + cycle) / cycle));
    for (var c = 0; c < copiesNeeded; c++) cloneSet(track, items);

    track.style.setProperty('--modulo-as-cycle-width', cycle + 'px');
    track.dataset.moduloAsReady = '1';
  }

  function init(el) {
    if (reduced()) return;
    var track = el.querySelector('.modulo-as__track');
    if (!track || track.dataset.moduloAsReady === '1') return;
    build(el);
  }

  function all() { return document.querySelectorAll('[data-modulo-as]'); }
  function rebuildAll() {
    var els = all();
    for (var i = 0; i < els.length; i++) build(els[i]);
  }

  /* Reduced motion turned ON mid-session: strip the clones we injected and
     clear ready, so the CSS wrap-and-centre path gets the ORIGINAL messages
     instead of the whole cloned train. Turned OFF: build as normal. */
  function onReduceChange() {
    var els = all();
    if (reduced()) {
      for (var i = 0; i < els.length; i++) {
        var track = els[i].querySelector('.modulo-as__track');
        if (!track) continue;
        dropClones(track);
        delete track.dataset.moduloAsReady;
        track.style.removeProperty('--modulo-as-cycle-width');
      }
    } else {
      rebuildAll();
    }
  }

  function boot() {
    var els = all();
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Document/window listeners must be wired exactly ONCE. Each section instance
     emits its own identical <script src>, and an identical src still EXECUTES
     once per tag — so two Statement Bars on a page would otherwise stack two of
     every listener, and each rebuildAll would run twice. boot() itself stays
     outside the guard so a later instance still initialises. */
  if (!window.__moduloAsWired) {
    window.__moduloAsWired = true;

    // Theme editor re-renders the section on every setting change.
    document.addEventListener('shopify:section:load', boot);

    /* Webfonts land AFTER this deferred script runs, and a font swap changes
       every item's width — leaving the cycle width measured against the
       fallback face and seaming the loop on every pass. */
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(rebuildAll).catch(function () {});
    }

    if (MQ) {
      if (MQ.addEventListener) MQ.addEventListener('change', onReduceChange);
      else if (MQ.addListener) MQ.addListener(onReduceChange); // Safari < 14
    }

    /* Re-measure on resize rather than only topping up clones. Crossing a
       breakpoint changes gap (<=480px) or font-size (<=860px), so the cycle
       width measured at load goes stale and the loop shows a seam every pass —
       a phone rotated to landscape (390 -> 844) crosses both. */
    var resizeTimer = null;
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (reduced()) return;
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (window.innerWidth === lastWidth) return; // vertical-only (mobile URL bar)
        lastWidth = window.innerWidth;
        rebuildAll();
      }, 200);
    });
  }
})();
